// C:\Projects\texe-web\src\pages\PostDetail.tsx
import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { apiGet, apiPost, apiDelete } from "../lib/api";
import { apiPatch } from "../lib/api";
import { type PostItem } from "../components/PostCard";
import { useAuth } from "../context/AuthProvider";
import { useGlobalPostInteractions } from "../context/PostInteractionsProvider";
import RichText from "../components/RichText";
import "../pages/Feed.css";

interface Comment {
  id: string;
  text: string;
  createdAt: string;
  author?: {
    id?: string;
    username?: string;
    avatarUrl?: string | null;
    avatarUrlSmall?: string | null;
    isAdmin?: boolean;
    totalStars?: number;
  };
  likeCount?: number;
  liked?: boolean;
}

export default function PostDetail() {
  const { postId = "" } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { toggleLike: toggleLikeHook, batchLoadInteractions, getInteraction } = useGlobalPostInteractions();

  // SavWar post mu kontrol et
  const isSavWarPost = location.pathname.startsWith('/savwar/post/');

  const [item, setItem] = useState<PostItem | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [failedAvatars, setFailedAvatars] = useState<Set<string>>(new Set());
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingCommentText, setEditingCommentText] = useState("");

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        // SavWar postu için farklı endpoint kullan
        const endpoint = isSavWarPost 
          ? `/api/savwar-posts/posts/${postId}`
          : `/api/posts/item/${postId}`;
        
        const res = await apiGet<{ ok: true; item: PostItem }>(endpoint);
        if (!ignore) {
          setItem(res.item);
          await batchLoadInteractions([postId]);
        }
      } catch (err) {
        console.error("Post load error:", err);
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => {
      ignore = true;
    };
  }, [postId, isSavWarPost, batchLoadInteractions]);

  const loadCommentLikeStates = useCallback(async (commentIds: string[]) => {
    if (!user || commentIds.length === 0) return;

    try {
      const res = await apiPost<{
        ok: true;
        comments: Array<{ id: string; count: number; liked: boolean }>;
      }>("/api/likes/state/batch", { commentIds });

      setComments((prev) =>
        prev.map((comment) => {
          const likeState = res.comments.find((s) => s.id === comment.id);
          if (likeState) {
            return {
              ...comment,
              likeCount: likeState.count,
              liked: likeState.liked,
            };
          }
          return comment;
        })
      );
    } catch (err) {
      console.error("Comment like states load error:", err);
    }
  }, [user]);

  const loadComments = useCallback(async () => {
    setCommentsLoading(true);
    try {
      // SavWar postu için farklı endpoint kullan
      const endpoint = isSavWarPost
        ? `/api/savwar-posts/posts/${postId}/comments`
        : `/api/comments/${postId}`;
      
      const res = await apiGet<{ ok: true; items: Comment[] }>(endpoint);
      const loadedComments = res.items || [];
      setComments(loadedComments);
      
      // Load like states for comments if user is logged in
      // SavWar yorumları zaten like state ile gelir, sadece normal postlar için yükle
      if (!isSavWarPost && user && loadedComments.length > 0) {
        await loadCommentLikeStates(loadedComments.map(c => c.id));
      }
    } catch (err) {
      console.error("Comments load error:", err);
    } finally {
      setCommentsLoading(false);
    }
  }, [postId, isSavWarPost, user, loadCommentLikeStates]);

  useEffect(() => {
    if (!postId) return;
    loadComments();
  }, [postId, loadComments]);

  // Reload comment like states when user changes
  useEffect(() => {
    // SavWar yorumları için like state ayrı endpoint'ten gelir
    if (!isSavWarPost && user && comments.length > 0) {
      loadCommentLikeStates(comments.map(c => c.id));
    }
  }, [user, comments.length, isSavWarPost, loadCommentLikeStates]);

  const toggleCommentLike = async (commentId: string) => {
    if (!user) {
      alert("Beğenmek için giriş yapmalısınız");
      return;
    }

    const comment = comments.find((c) => c.id === commentId);
    if (!comment) return;

    const wasLiked = comment.liked || false;
    const currentCount = comment.likeCount || 0;

    // Optimistic update
    setComments((prev) =>
      prev.map((c) =>
        c.id === commentId
          ? {
              ...c,
              liked: !wasLiked,
              likeCount: wasLiked ? Math.max(0, currentCount - 1) : currentCount + 1,
            }
          : c
      )
    );

    try {
      // SavWar için farklı endpoint
      if (isSavWarPost) {
        if (wasLiked) {
          await apiDelete(`/api/savwar-posts/comments/${commentId}/like`);
        } else {
          await apiPost(`/api/savwar-posts/comments/${commentId}/like`, {});
        }
      } else {
        if (wasLiked) {
          await apiDelete(`/api/likes/comment/${commentId}`);
        } else {
          await apiPost(`/api/likes/comment/${commentId}`, {});
        }
      }
    } catch (err) {
      console.error("Comment like toggle error:", err);
      // Revert on error
      setComments((prev) =>
        prev.map((c) =>
          c.id === commentId
            ? { ...c, liked: wasLiked, likeCount: currentCount }
            : c
        )
      );
    }
  };

  const handleSubmitComment = async () => {
    if (!commentText.trim() || submitting) return;

    setSubmitting(true);
    try {
      // SavWar için farklı endpoint
      const endpoint = isSavWarPost
        ? `/api/savwar-posts/posts/${postId}/comments`
        : `/api/comments`;
      
      const payload = isSavWarPost
        ? { text: commentText.trim() }
        : { postId: postId, text: commentText.trim() };
      
      const res = await apiPost<{ ok: true; item: Comment }>(endpoint, payload);
      
      const newComment = {
        ...res.item,
        likeCount: 0,
        liked: false,
      };
      
      setComments((prev) => [newComment, ...prev]);
      setCommentText("");
    } catch (err) {
      console.error("Comment submit error:", err);
      alert("Yorum gönderilemedi");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    const comment = comments.find((c) => c.id === commentId);
    const isMyComment = comment?.author?.id === user?.id;
    const isAdmin = user?.isAdmin;
    
    const confirmMessage = isAdmin && !isMyComment
      ? "Bu yorumu silmek istediğinizden emin misiniz? (Admin Yetkisi)"
      : "Bu yorumu silmek istediğinizden emin misiniz?";
    
    if (!window.confirm(confirmMessage)) return;

    try {
      // SavWar için farklı endpoint
      const endpoint = isSavWarPost
        ? `/api/savwar-posts/comments/${commentId}`
        : `/api/comments/${commentId}`;
      
      await apiDelete(endpoint);
      setComments((prev) => prev.filter((c) => c.id !== commentId));
    } catch (err) {
      console.error("Comment delete error:", err);
      alert("Yorum silinemedi");
    }
  };

  const handleEditComment = (comment: Comment) => {
    setEditingCommentId(comment.id);
    setEditingCommentText(comment.text);
  };

  const handleSaveComment = async (commentId: string) => {
    if (!editingCommentText.trim()) return;

    try {
      // SavWar için farklı endpoint
      const endpoint = isSavWarPost
        ? `/api/savwar-posts/comments/${commentId}`
        : `/api/comments/${commentId}`;
      
      await apiPatch(endpoint, {
        text: editingCommentText.trim(),
      });
      setComments((prev) =>
        prev.map((c) => (c.id === commentId ? { ...c, text: editingCommentText.trim() } : c))
      );
      setEditingCommentId(null);
      setEditingCommentText("");
    } catch (err) {
      console.error("Comment save error:", err);
      alert("Yorum güncellenemedi");
    }
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditingCommentText("");
  };

  const toggleLike = useCallback(
    async (postId: string) => {
      if (!user) return;
      try {
        await toggleLikeHook(postId);
      } catch (err) {
        console.error("Like toggle error:", err);
      }
    },
    [user, toggleLikeHook]
  );

  const getUserInitial = (username?: string) => {
    return username ? username.charAt(0).toUpperCase() : "?";
  };

  const handleAvatarError = (userId: string) => {
    setFailedAvatars((prev) => new Set(prev).add(userId));
  };

  if (loading) return <div style={{ padding: 24 }}>Yükleniyor...</div>;
  if (!item) return <div style={{ padding: 24 }}>Gönderi bulunamadı.</div>;

  const interaction = getInteraction(postId);
  const avatarFailed = item.author?.id ? failedAvatars.has(item.author.id) : false;

  return (
    <div className="feed-container">
      <div className="feed-content">
        {/* Back Button */}
        <div style={{ marginBottom: 16 }}>
          <button
            className="feed-button"
            onClick={() => navigate(-1)}
            style={{ display: "flex", alignItems: "center", gap: 8 }}
          >
            ← Geri
          </button>
        </div>

        {/* Post Card */}
        <div
          style={{
            position: "relative",
            background: "#1a1a1a",
            border: "2px solid #114232",
            borderRadius: 16,
            padding: 16,
            marginBottom: 24,
          }}
        >
          {/* Post Header */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
            {/* Avatar */}
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                overflow: "hidden",
                cursor: "pointer",
                border: item.author?.isAdmin ? "3px solid #FFD700" : "2px solid #114232",
              }}
              onClick={() => item.author?.id && navigate(`/profile/${item.author.id}`)}
            >
              {avatarFailed || !item.author?.avatarUrlSmall ? (
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    background: "linear-gradient(135deg, #114232, #1a5a42)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    fontSize: 20,
                    fontWeight: 700,
                  }}
                >
                  {getUserInitial(item.author?.username)}
                </div>
              ) : (
                <img
                  src={item.author.avatarUrlSmall}
                  alt={item.author.username}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  onError={() => item.author?.id && handleAvatarError(item.author.id)}
                />
              )}
            </div>

            {/* User info */}
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span
                  style={{
                    fontWeight: 700,
                    color: item.author?.isAdmin ? "#FFD700" : "#114232",
                    cursor: "pointer",
                  }}
                  onClick={() => item.author?.id && navigate(`/profile/${item.author.id}`)}
                >
                  {item.author?.username || "Anonim"}
                  {item.author?.totalStars && item.author.totalStars > 0 && (
                    <span style={{ color: "#FFD700", fontSize: 13, marginLeft: 4 }}>
                      ⭐{item.author.totalStars.toFixed(1)}
                    </span>
                  )}
                </span>
                {item.author?.isAdmin && (
                  <span
                    style={{
                      fontSize: 11,
                      padding: "2px 6px",
                      background: "#FFD700",
                      color: "#000",
                      borderRadius: 4,
                      fontWeight: 700,
                    }}
                  >
                    ADMIN
                  </span>
                )}
              </div>
              <div style={{ fontSize: 12, color: "#666" }}>
                {new Date(item.createdAt).toLocaleString("tr-TR")}
              </div>
            </div>
          </div>

          {/* Post Content */}
          <div
            style={{
              fontSize: 15,
              lineHeight: 1.5,
              color: "#fff",
              marginBottom: 12,
            }}
          >
            <RichText text={item.text} />
          </div>

          {/* Post Actions */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 16,
              marginTop: 12,
              paddingTop: 12,
              borderTop: "1px solid #2c2c2c",
            }}
          >
            {/* Like */}
            <button
              onClick={() => toggleLike(postId)}
              disabled={interaction.loading}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 12px",
                borderRadius: 6,
                border: interaction.liked ? "1px solid #114232" : "1px solid #666",
                background: interaction.liked ? "rgba(17, 66, 50, 0.2)" : "transparent",
                color: interaction.liked ? "#114232" : "#666",
                fontSize: 14,
                fontWeight: 600,
                cursor: interaction.loading ? "not-allowed" : "pointer",
                opacity: interaction.loading ? 0.6 : 1,
              }}
            >
              <span>{interaction.liked ? "❤️" : "🤍"}</span>
              <span>{interaction.likeCount}</span>
            </button>

            {/* Comments Count */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 12px",
                borderRadius: 6,
                border: "1px solid #666",
                color: "#666",
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              <span>💬</span>
              <span>{comments.length}</span>
            </div>

            {/* Share */}
            <button
              onClick={() => {
                const url = `${window.location.origin}/post/${postId}`;
                navigator.clipboard.writeText(url).then(() => {
                  alert("Link kopyalandı!");
                }).catch(() => {
                  alert("Link kopyalanamadı");
                });
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 12px",
                borderRadius: 6,
                border: "1px solid #666",
                background: "transparent",
                color: "#666",
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              <span>🔗</span>
            </button>
          </div>
        </div>

        {/* Comments Section */}
        <div
          style={{
            background: "#1a1a1a",
            border: "2px solid #114232",
            borderRadius: 16,
            padding: 16,
          }}
        >
          <h2 style={{ margin: 0, marginBottom: 16, color: "#114232", fontSize: 20 }}>
            Yorumlar ({comments.length})
          </h2>

          {/* Comment Input */}
          {user && (
            <div style={{ marginBottom: 24 }}>
              <textarea
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder="Yorumunuzu yazın..."
                style={{
                  width: "100%",
                  minHeight: 80,
                  padding: 12,
                  borderRadius: 8,
                  border: "2px solid #114232",
                  background: "#0a0a0a",
                  color: "#fff",
                  fontSize: 14,
                  fontFamily: "inherit",
                  resize: "vertical",
                  boxSizing: "border-box",
                }}
              />
              <button
                className="feed-button feed-button-primary"
                onClick={handleSubmitComment}
                disabled={!commentText.trim() || submitting}
                style={{ marginTop: 8 }}
              >
                {submitting ? "Gönderiliyor..." : "Yorum Yap"}
              </button>
            </div>
          )}

          {/* Comments List */}
          {commentsLoading ? (
            <div style={{ textAlign: "center", padding: 24, color: "#666" }}>
              Yorumlar yükleniyor...
            </div>
          ) : comments.length === 0 ? (
            <div style={{ textAlign: "center", padding: 24, color: "#666" }}>
              Henüz yorum yapılmamış. İlk yorumu siz yapın!
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {comments.map((comment) => {
                const commentAvatarFailed = comment.author?.id
                  ? failedAvatars.has(comment.author.id)
                  : false;
                const isMyComment = comment.author?.id === user?.id;

                return (
                  <div
                    key={comment.id}
                    style={{
                      padding: 12,
                      background: "#0a0a0a",
                      borderRadius: 8,
                      border: "1px solid #2c2c2c",
                    }}
                  >
                    {/* Comment Header */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        marginBottom: 8,
                      }}
                    >
                      {/* Avatar */}
                      <div
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          overflow: "hidden",
                          cursor: "pointer",
                          border: comment.author?.isAdmin
                            ? "2px solid #FFD700"
                            : "1px solid #114232",
                        }}
                        onClick={() =>
                          comment.author?.id && navigate(`/profile/${comment.author.id}`)
                        }
                      >
                        {commentAvatarFailed || !comment.author?.avatarUrlSmall ? (
                          <div
                            style={{
                              width: "100%",
                              height: "100%",
                              background: "linear-gradient(135deg, #114232, #1a5a42)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#fff",
                              fontSize: 14,
                              fontWeight: 700,
                            }}
                          >
                            {getUserInitial(comment.author?.username)}
                          </div>
                        ) : (
                          <img
                            src={comment.author.avatarUrlSmall}
                            alt={comment.author.username}
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                            onError={() =>
                              comment.author?.id && handleAvatarError(comment.author.id)
                            }
                          />
                        )}
                      </div>

                      {/* User Info */}
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span
                            style={{
                              fontWeight: 700,
                              fontSize: 14,
                              color: comment.author?.isAdmin ? "#FFD700" : "#114232",
                              cursor: "pointer",
                            }}
                            onClick={() =>
                              comment.author?.id && navigate(`/profile/${comment.author.id}`)
                            }
                          >
                            {comment.author?.username || "Anonim"}
                          </span>
                          {comment.author?.isAdmin && (
                            <span
                              style={{
                                fontSize: 9,
                                padding: "2px 4px",
                                background: "#FFD700",
                                color: "#000",
                                borderRadius: 3,
                                fontWeight: 700,
                              }}
                            >
                              ADMIN
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 11, color: "#666" }}>
                          {new Date(comment.createdAt).toLocaleString("tr-TR")}
                        </div>
                      </div>

                      {/* Delete/Edit Buttons */}
                      {(user?.isAdmin || isMyComment) && (
                        <div style={{ display: "flex", gap: 4 }}>
                          {(isMyComment || user?.isAdmin) && editingCommentId !== comment.id && (
                            <button
                              onClick={() => handleEditComment(comment)}
                              style={{
                                padding: "4px 8px",
                                borderRadius: 4,
                                border: "1px solid #114232",
                                background: "transparent",
                                color: "#114232",
                                fontSize: 12,
                                cursor: "pointer",
                              }}
                            >
                              {user?.isAdmin && !isMyComment ? "Düzenle (Admin)" : "Düzenle"}
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteComment(comment.id)}
                            style={{
                              padding: "4px 8px",
                              borderRadius: 4,
                              border: "1px solid #dc2626",
                              background: "transparent",
                              color: "#dc2626",
                              fontSize: 12,
                              cursor: "pointer",
                            }}
                          >
                            {user?.isAdmin && !isMyComment ? "Sil (Admin)" : "Sil"}
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Comment Text */}
                    {editingCommentId === comment.id ? (
                      <div style={{ marginTop: 8 }}>
                        <textarea
                          value={editingCommentText}
                          onChange={(e) => setEditingCommentText(e.target.value)}
                          autoFocus
                          style={{
                            width: "100%",
                            minHeight: 60,
                            padding: 8,
                            borderRadius: 6,
                            border: "2px solid #114232",
                            background: "#000",
                            color: "#fff",
                            fontSize: 14,
                            fontFamily: "inherit",
                            resize: "vertical",
                          }}
                        />
                        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                          <button
                            onClick={() => handleSaveComment(comment.id)}
                            className="feed-button feed-button-primary"
                            style={{ fontSize: 13, padding: "6px 12px" }}
                            disabled={!editingCommentText.trim()}
                          >
                            Kaydet
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="feed-button"
                            style={{ fontSize: 13, padding: "6px 12px" }}
                          >
                            İptal
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div
                          style={{
                            fontSize: 14,
                            lineHeight: 1.4,
                            color: "#fff",
                            marginBottom: 8,
                          }}
                        >
                          <RichText text={comment.text} />
                        </div>

                        {/* Comment Like Button */}
                        <button
                          onClick={() => toggleCommentLike(comment.id)}
                          disabled={!user}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            padding: "4px 10px",
                            borderRadius: 6,
                            border: comment.liked
                              ? "1px solid #dc2626"
                              : "1px solid #666",
                            background: comment.liked
                              ? "rgba(220, 38, 38, 0.1)"
                              : "transparent",
                            color: comment.liked ? "#dc2626" : "#666",
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: user ? "pointer" : "not-allowed",
                            opacity: user ? 1 : 0.5,
                          }}
                          title={!user ? "Beğenmek için giriş yapın" : ""}
                        >
                          <span>{comment.liked ? "❤️" : "🤍"}</span>
                          <span>{comment.likeCount || 0}</span>
                        </button>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
